<i18n lang='yml' src='../i18n/SubmitSurvey.yml'></i18n>

<template>
  <div v-if="loading">
    <loading-animation-vue></loading-animation-vue>
  </div>
 <div v-else>
   <div class="border-bottom" v-for="question in questions" :key="question.position">
     <input type="hidden" :name="`survey_responses[responses][${question.id}][id]`" :value="question.id"/>
     <input type="hidden" :name="`survey_responses[responses][${question.id}][response_text]`" value=""/>
     <input type="hidden" :name="`survey_responses[responses][${question.id}][validation_type]`"
            :value="question.type"/>
     <input type="hidden" :name="`survey_responses[responses][${question.id}][survey_question_id]`"
            :value="question.id"/>

     <div class="row">
       <div class="col-sm-12 col-md-8 col-lg-6">
         <div class="form-group">
           <template v-if="questionIsSelectOne(question)">
             <fieldset>
               <legend><strong v-if="question.required" style="color: red">* <span
                 class="sr-only">Required</span></strong>{{ question.title }}
               </legend>
               <div class="radio" v-for="answer in question.answers" v-bind:key="answer.id">
                 <label>
                   <input type="radio" :name="`survey_responses[responses][${question.id}][survey_answer_id]`" :value="answer.id"
                          v-model="responses[question.id].survey_answer_id"> {{ answer.answer_text }}
                 </label>
               </div>
             </fieldset>
           </template>
           <template v-else-if="questionIsSelectMultiple(question)">
             <label :for="`survey_responses[responses][${question.id}][survey_answer_id]`">
               <strong v-if="question.required" style="color: red">
                 * <span class="sr-only">Required</span>
               </strong>
               {{ question.title }}
             </label>
             <select :id="`survey_responses[responses][${question.id}][survey_answer_id]`"
                     :name="`survey_responses[responses][${question.id}][survey_answer_id]`" class="form-control"
                     v-model="responses[question.id].survey_answer_id">
               <option v-for="answer in question.answers" v-bind:key="answer.id" v-bind:value="answer.id">
                 {{ answer.answer_text }}
               </option>
             </select>
           </template>
           <template v-else-if="questionIsTextEntry(question)">
             <label :for="`survey_responses[responses][${question.id}][response_text]`">
               <strong v-if="question.required" style="color: red">
                 * <span class="sr-only">Required</span>
               </strong>
               {{ question.title }}
               <input type="text" :id="`survey_responses[responses][${question.id}][response_text]`"
                      :name="`survey_responses[responses][${question.id}][response_text]`"
                      v-model="responses[question.id].response_text" class="form-control"/>
             </label>
           </template>
           <div class="text-danger" v-if="errors[question.id] && errors[question.id].message">
             {{ errors[question.id].message }}
           </div>
         </div>
       </div>
     </div>
   </div>
   <div class="row">
     <div class="col-sm-12">
       <div class="border-bottom light clearfix">
         <div>
           <button type="submit" class="btn btn-sm btn-success">
             <font-awesome-icon :icon="['fas', 'save']" />
             {{ $t('submit_answers') }}
           </button>
         </div>
       </div>
     </div>
   </div>
 </div>
</template>

<script>
  import {REGEXP} from "../../shared/util";
  import { sendAjaxRequest } from '../../shared/typescripts/ajax.ts';

  export default {
    props: {
      api_path: {type: String},
      load_path: {type: String},
      user_id: {type: String},
      form_id: {type: String}
    },

    data() {
      return {
        loading: true,
        questions: [],
        responses: {},
        errors: {},
      };
    },

    methods: {
      questionIsSelectOne: question => question.type === "select_one",
      questionIsSelectMultiple: question => question.type === "select_multiple",
      questionIsTextEntry: question => question.type === "text_entry",

      fetchQuestions() {
        sendAjaxRequest('GET', this.load_path, 'json', {}, this.fetchSuccess, this.fetchError);
      },

      fetchSuccess(response) {
        this.loadQuestionData(response);
        this.fixSelect2Dropdowns();
        this.loading = false;
      },

      fetchError() {
        alert("Error loading onboarding questions. :(");
        console.log(response);
      },

      loadQuestionData(data) {
        let question_ids = [];
        this.questions = [];

        // Use the API data and initialize the questions we have to show
        data.questions.forEach(item => {
          this.questions.push({
            id: item.id,
            position: item.position,
            survey_question_id: item.survey_question.id,
            title: item.survey_question.question_text,
            type: item.survey_question.question_type,
            required: item.survey_question.required,
            validation_type: item.survey_question.validation_type,
            answers: item.survey_question.survey_answers,
          });

          question_ids.push(item.id);

          this.responses[item.id] =  {
            id: null,
            survey_question_id: item.survey_question.id,
            survey_answer_id: null,
            required: item.survey_question.required
          };
          
          this.questions.sort((a,b) => a.position - b.position);
        });

        // Initialize all responses as blank for now.
        question_ids.forEach(item => {
          if (!data.hasOwnProperty(item)) {
            this.responses[item.id] = {
              id: null,
              question_id: item.id,
              answer_id: null,
              response_text: null
            };
          }
        });
      },

      validateResponseData() {
        this.errors = {};

        this.questions.forEach(question => {
          let response = this.responses[question.id];

          let isResponseTextEmpty = (text) => {
            return !text || text.trim() === "";
          };

          if (question.required) {
            if (this.questionIsTextEntry(question) && isResponseTextEmpty(response.response_text)) {
              this.errors[question.id] = {
                message: "Question is required."
              };

              return; // This error message is more important than anything else we could possibly whine about.
            }
          }

          if (question.validation_type && this.questionIsTextEntry(question)) {
            if (question.validation_type === 'email' && !isResponseTextEmpty(response.response_text)) {
              if (!REGEXP.email.test(response.response_text)) {
                this.errors[question.id] = {
                  message: "Invalid email address"
                };
              }
            } else if (question.validation_type === 'date' && !isResponseTextEmpty(response.response_text)) {
              if (!REGEXP.date.test(response.response_text)) {
                this.errors[question.id] = {
                  message: "Invalid date"
                };
              }
            } else if (question.validation_type === 'phone' && !isResponseTextEmpty(response.response_text)) {
              if (!REGEXP.phone.test(response.response_text)) {
                this.errors[question.id] = {
                  message: "Invalid phone number"
                };
              }
            }
          }
        });
      },

      submitSurvey(event) {
        event.preventDefault();
        event.stopPropagation();
        this.validateResponseData();

        if (Object.keys(this.errors).length === 0) {
          let payload = {
            survey_responses: {
              user_id: this.user_id,
              responses: [],
            },
          };

          this.questions.forEach((question) => {
            const questionId = question.id;
            const response = this.responses[questionId];

            payload.survey_responses.responses.push({
              id: response.id,
              survey_question_id: response.survey_question_id,
              survey_answer_id: response.survey_answer_id,
              response_text: response.response_text,
            });
          });
          sendAjaxRequest('PUT', `${this.api_path}.json`, 'json', payload, this.submitSuccess, this.submitError)
        }
      },

      submitSuccess(response) {
        window.location = response.redirect_uri;
      },
      submitError(response) {
        alert(this.$t('error_saving'));
        console.log(response);
      },

      fixSelect2Dropdowns() {
        const self = this;
        this.$nextTick(() => {
          var options = {
            theme: "bootstrap",
            multiple: true
          };

          Object.keys(this.$refs).forEach(k => {
            console.log(this.$refs[k]);
            $(self.$refs[k]).select2(options);
          });
        });
      }
    },

    mounted() {
      $(`#${this.form_id}`).on("submit", this.submitSurvey);
      this.fetchQuestions();
    }
  };
</script>
